import { useEffect } from 'react'
import L from 'leaflet'
import {
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import './locationPicker.css'

export interface Coordinates {
  lat: number
  lng: number
}

/** Fortaleza, onde estão as escolas hoje. Só vale quando não há nada escolhido. */
const DEFAULT_CENTER: Coordinates = { lat: -3.7319, lng: -38.5267 }
const DEFAULT_ZOOM = 13
/** Zoom de quadra: é a precisão que faz sentido para "a porta da escola". */
const PICKED_ZOOM = 17

interface LocationPickerProps {
  value: Coordinates | null
  onChange: (coords: Coordinates) => void
  /** Copy do rodapé — cada tela ajusta ao que está localizando (escola, página do criador...). */
  hint?: string
}

const round6 = (value: number) => Number(value.toFixed(6))

/** Um alfinete simples, sem depender dos ícones default do Leaflet (que quebram no bundler). */
const PIN = L.divIcon({
  className: 'bw-picker-pin',
  html: '<span class="bw-picker-pin__dot"></span>',
  iconSize: [0, 0],
  iconAnchor: [0, 0],
})

function ClickToPlace({
  onChange,
}: {
  onChange: (coords: Coordinates) => void
}) {
  useMapEvents({
    click: (event) => {
      onChange({ lat: round6(event.latlng.lat), lng: round6(event.latlng.lng) })
    },
  })
  return null
}

/**
 * Reposiciona quando a coordenada muda por fora do mapa — geocodificação pelo CEP
 * ou digitação manual. Sem isto, o admin busca o endereço e o mapa continua onde
 * estava, parecendo que a busca não funcionou.
 */
function RecenterOnValue({ value }: { value: Coordinates | null }) {
  const map = useMap()

  useEffect(() => {
    if (!value) return
    map.setView([value.lat, value.lng], Math.max(map.getZoom(), PICKED_ZOOM))
  }, [map, value])

  return null
}

/**
 * O container é criado uma vez só e nunca recebe center/zoom novos — o Leaflet
 * ignora mudanças nessas props depois de montado, e é o RecenterOnValue que cuida
 * do resto. Sem o invalidateSize o mapa nasce cinza quando o card abre fechado.
 */
function FixSizeOnMount() {
  const map = useMap()

  useEffect(() => {
    const frame = requestAnimationFrame(() => map.invalidateSize())
    return () => cancelAnimationFrame(frame)
  }, [map])

  return null
}

const DEFAULT_HINT =
  'Clique no mapa ou arraste o marcador para ajustar o ponto exato da escola.'

export default function LocationPicker({
  value,
  onChange,
  hint = DEFAULT_HINT,
}: LocationPickerProps) {
  const center = value ?? DEFAULT_CENTER

  return (
    <div className="overflow-hidden rounded-lg border">
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={value ? PICKED_ZOOM : DEFAULT_ZOOM}
        scrollWheelZoom
        className="h-72 w-full"
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
          maxZoom={19}
        />

        <ClickToPlace onChange={onChange} />
        <RecenterOnValue value={value} />
        <FixSizeOnMount />

        {value && (
          <Marker
            position={[value.lat, value.lng]}
            icon={PIN}
            draggable
            eventHandlers={{
              dragend: (event) => {
                const { lat, lng } = event.target.getLatLng()
                onChange({ lat: round6(lat), lng: round6(lng) })
              },
            }}
          />
        )}
      </MapContainer>

      <p className="border-t bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        {hint}
      </p>
    </div>
  )
}
